// =============================================================================
// ReportToolbar — the one toolbar for a printable report page (farm phone /
// tablet spec W-17: X-06, SC-20, SC-22, HV-13).
//
// The report pages each hand-rolled the same sticky bar and pinned EVERYTHING
// in it: title, week picker, date range, facet selects, print ticks, the amber
// "12 route maps couldn't be loaded" notes, and Print. Measured on 23 Sep: the
// scout report pinned 349px of a phone once its options were ticked, and the
// harvest report 237px of a portrait tablet and 237 of a 390px-tall phone on
// its side — the report itself got what was left.
//
// So this pins ONE row — Back · title · Help (when the route has a topic, the
// <PageHeader> rule) · the page's print control — and puts everything else in
// a band under it that SCROLLS AWAY with the page:
//   - below md, the filters and print options fold behind "Filters (n)",
//     closed on arrival. n is how many filters differ from what the report
//     opens on (`activeFilterCount`), so a narrowed sheet still says so while
//     folded. Print options (Photos, Walk routes…) are not filters and are not
//     counted — the preview under the toolbar shows them.
//   - from md, the same band lays them out inline, always open. Still not
//     pinned: two rows of selects pinned on a tablet is the defect.
//   - `secondary` (a link to a sibling report) and `notes` (the page telling
//     you its print will be incomplete) are never behind the fold — a warning
//     you have to open a disclosure to find is not a warning — but they scroll
//     away with the band.
// Print control: the page's own <PrintGateButton/> / <DocumentPrintGate/>,
// unchanged. The row wraps rather than overflowing sideways when a long gate
// label ("Can't print — the scouting record didn't load") needs the width.
//
// The whole toolbar is print:hidden. The A4 article under it — the signed
// document — does not change.
// =============================================================================

import { useId, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { ChevronDown, SlidersHorizontal } from 'lucide-react'
import BackButton from '@/components/BackButton'
import HelpButton from '@/components/HelpButton'
import { resolveHelpTopicId } from '@/lib/helpTopics'

// Light = the white toolbar over a printable A4 preview (the common case);
// dark = a report drawn on the app shell (the drain record). Same prop name
// and idea as <Modal surface/> and <QueryStateGate surface/>.
const SURFACES = {
  light: {
    bar:    'bg-white text-gray-900 border-gray-300',
    toggle: 'bg-white border-gray-300 text-gray-900 hover:bg-gray-100',
    sub:    'text-gray-500',
  },
  dark: {
    bar:    'surface-sunk text-gray-100 light:text-fg hairline',
    toggle: 'surface hairline-strong text-gray-200 light:text-fg light:hover:bg-raised hover:bg-gray-700',
    sub:    'text-muted',
  },
} as const

/** How many of a report's filters differ from what it opens on — the (n). */
export function activeFilterCount(...changed: boolean[]): number {
  return changed.filter(Boolean).length
}

/** The disclosure's label: "Filters", or "Filters (2)" once two are set. */
export function filtersLabel(active: number): string {
  return active > 0 ? `Filters (${active})` : 'Filters'
}

export interface ReportToolbarProps {
  /** The page's name — the h1. The tab title stays with the page (usePageTitle). */
  title: string
  /** Decorative icon before the title; dropped below sm, where the width is the title's. */
  icon?: ReactNode
  /** One line under the title, truncated. */
  subtitle?: ReactNode
  /** Back behaviour, as <PageHeader>: onBack > backTo > smart back. */
  onBack?: () => void
  backTo?: string
  /** PINNED: the print control (and at most an icon beside it) — the only thing
   *  that must stay reachable mid-document. Keep it to one control's width. */
  actions: ReactNode
  /** The filters and print options. Folded below md, inline from md; never pinned. */
  children?: ReactNode
  /** Filters changed from the report's defaults — see activeFilterCount. */
  activeFilters?: number
  /** Always visible, never pinned: a link to a sibling report. */
  secondary?: ReactNode
  /** Always visible, never pinned: the page's warnings about what will print. */
  notes?: ReactNode
  surface?: keyof typeof SURFACES
  /** Opt out of the route-resolved help button (the page owns its help UI). */
  noHelp?: boolean
}

type Surface = (typeof SURFACES)[keyof typeof SURFACES]

export default function ReportToolbar({
  title, icon, subtitle, onBack, backTo, actions, children, activeFilters = 0,
  secondary, notes, surface = 'light', noHelp = false,
}: ReportToolbarProps) {
  const { pathname } = useLocation()
  const s = SURFACES[surface]
  const showHelp = !noHelp && resolveHelpTopicId(pathname) !== undefined
  const hasFilters = children != null && children !== false
  return (
    <>
      <PinnedRow title={title} icon={icon} subtitle={subtitle} onBack={onBack} backTo={backTo}
        actions={actions} showHelp={showHelp} s={s} />
      {(hasFilters || secondary || notes) && (
        <ScrollAwayBand hasFilters={hasFilters} activeFilters={activeFilters}
          secondary={secondary} notes={notes} s={s}>
          {children}
        </ScrollAwayBand>
      )}
    </>
  )
}

/** The ONLY pinned part. `sticky-top-inset` carries the notch / banner inset
 *  the way <PageHeader> does, so it never pins under a status bar. */
function PinnedRow({ title, icon, subtitle, onBack, backTo, actions, showHelp, s }:
  Pick<ReportToolbarProps, 'title' | 'icon' | 'subtitle' | 'onBack' | 'backTo' | 'actions'> & { showHelp: boolean; s: Surface }) {
  return (
    <div data-report-toolbar="pinned"
      className={`sticky top-0 sticky-top-inset z-10 border-b px-4 pb-2 flex flex-wrap items-center gap-2 sm:gap-3 print:hidden ${s.bar}`}>
      <BackButton onClick={onBack} to={backTo} surface={s === SURFACES.light ? 'light' : 'shell'} />
      {/* basis-0: the title yields width before the print control wraps;
          min-w-[7rem] is <PageHeader>'s floor, so it never collapses to "S…". */}
      <div className="flex-1 basis-0 min-w-[7rem]">
        <h1 className="text-base sm:text-lg font-bold flex items-center gap-2 min-w-0">
          {icon && <span aria-hidden="true" className="hidden sm:inline-flex shrink-0">{icon}</span>}
          <span className="truncate">{title}</span>
        </h1>
        {subtitle && <p className={`text-xs truncate ${s.sub}`}>{subtitle}</p>}
      </div>
      {showHelp && <HelpButton className="shrink-0" surface={s === SURFACES.light ? 'light' : 'shell'} />}
      <div className="ml-auto max-w-full flex items-center gap-2">{actions}</div>
    </div>
  )
}

/** Everything else — never pinned, gone from paper. */
function ScrollAwayBand({ children, hasFilters, activeFilters, secondary, notes, s }:
  Pick<ReportToolbarProps, 'children' | 'secondary' | 'notes'> & { hasFilters: boolean; activeFilters: number; s: Surface }) {
  const [open, setOpen] = useState(false)
  const panelId = useId()
  return (
    <div data-report-toolbar="band"
      className={`border-b px-4 py-2 flex flex-wrap items-center gap-2 sm:gap-3 print:hidden ${s.bar}`}>
      {hasFilters && (
        <FoldToggle open={open} onToggle={() => setOpen(v => !v)} panelId={panelId}
          activeFilters={activeFilters} className={s.toggle} />
      )}
      {secondary && <div className="flex items-center gap-2 md:order-1 empty:hidden">{secondary}</div>}
      {/* Below md: its own full-width row when open, gone when closed.
          From md: `contents`, so its controls flow in the band's one row. */}
      {hasFilters && (
        <div id={panelId} data-report-toolbar="filters"
          className={`${open ? 'flex' : 'hidden'} md:contents w-full flex-wrap items-center gap-2 sm:gap-3`}>
          {children}
        </div>
      )}
      {/* empty:hidden — a page passes its notes as a run of conditionals, and
          when none fires the row must not leave a gap behind. */}
      {notes && <div className="w-full order-2 space-y-1 empty:hidden">{notes}</div>}
    </div>
  )
}

/** "Filters (n)" — below md only; from md the filters are laid out inline. */
function FoldToggle({ open, onToggle, panelId, activeFilters, className }: {
  open: boolean; onToggle: () => void; panelId: string; activeFilters: number; className: string
}) {
  return (
    <button type="button" onClick={onToggle}
      aria-expanded={open} aria-controls={panelId}
      className={`md:hidden px-3 min-h-[44px] rounded-lg border flex items-center gap-1.5 text-sm ${className}`}>
      <SlidersHorizontal size={15} aria-hidden="true" />
      {filtersLabel(activeFilters)}
      <ChevronDown size={14} aria-hidden="true" className={`transition-transform${open ? ' rotate-180' : ''}`} />
    </button>
  )
}
